import create, { StoreApi, UseBoundStore } from 'zustand';
import { GetState, SetState, EqualityChecker, StateSelector } from 'types/zustand';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { getValidationIssues } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal/ValidationIssueService';
import { BreakLevel } from '@decisions/server/DecisionsFramework';
import { HelpCenterLocation } from './HelpCenterLocation';
import { ActionHeader, EntityActionType } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { getActionHeadersForAnyEntity } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/Common/CommonActionService';
import { ValidationEntry } from './ValidationEntry';
import { runFlowValidations } from '@decisions/server/DecisionsFramework/Design/Flow/Service/FlowEditService';
import { getFolderId, PortalStore, usePortalStore } from 'domains/portal/PortalStore';
import { asyncQueue } from 'utils/ApiUtils';
import { ClientMessage, registerForEventAndSubscribe, unRegisterForEventAndUnsubscribe } from 'utils/ClientEventUtils';

export interface IHelpCenterOptions {
  hasIgnoreAction?: boolean;
  showIgnored?: boolean;
  helpCenterLocation?: HelpCenterLocation;
  flowSessionId?: string;
}

export enum HelpChatState {
  CLOSED,
  OPEN,
  MINIMIZED,
}

const SystemValidationChangedEvent = 'SystemValidationChangedEvent';

/**
 * TS interface describing store
 */
export interface IHelpCenterStore extends IHelpCenterOptions {
  entries?: ValidationEntry[];
  warningsOpen: boolean;
  errorsOpen: boolean;
  toolsOpen: boolean;
  toolsActions: ActionHeader[];
  /**
   * actions on shared object should make it easier to spread off one selector in components,
   * or strip this property off in code-tests. Shouldn't be updated after create / cause extra renders.
   */
  actions: {
    getValidations: () => Promise<void>;
    toggleErrors: () => void;
    toggleWarnings: () => void;
    popOutLayer: () => void;
    reRunValidations: () => void;
    toggleTools: () => void;
    ignoreAction: (entry: ValidationEntry) => void;
    toggleIgnore: () => void;
    goToAction: (entry: ValidationEntry) => void;
  };
}

export interface IExampleSelector<U> {
  (selector: StateSelector<IHelpCenterStore, U>, equalityFn?: EqualityChecker<U>): U;
}

//#region store creation

const whatsOpen = { warningsOpen: false, errorsOpen: false, toolsOpen: false };
const defaultOptions = {
  hasIgnoreAction: false,
  showIgnored: false,
  helpCenterLocation: HelpCenterLocation.Portal,
};

/**
 * if you will have multiple instances of your store, a factory function will DRY creation up for you
 *
 * If you need state to be global / singleton, then just create it immediately and export / import it
 * (per standard zustand use-case.)
 */
export function createHelpCenterStore(id: string, options?: IHelpCenterOptions) {
  options = options ? { ...defaultOptions, ...options } : { ...defaultOptions };
  const useStore = create<IHelpCenterStore>((set, get) => ({
    entries: [],
    toolsActions: [],
    ...whatsOpen,
    ...options,
    actions: {
      getValidations: async () => {
        const issues = await getValidationIssues();
        // TODO this mapping could be a function since I'm doing it multiple times
        const entries = issues.map(issue => {
          return new ValidationEntry({
            id: issue.Id,
            title: issue.Name,
            description: issue.Details,
            validationItemType: issue.ValidationItemType,
            entityTypeName: issue.EntityTypeName,
            imageUrl: issue.ImageUrl,
            level: BreakLevel.Fatal,
          });
        });
        set({ entries });
      },
      toggleErrors: () => set({ ...whatsOpen, errorsOpen: !get().errorsOpen }),
      toggleWarnings: () => set({ ...whatsOpen, warningsOpen: !get().warningsOpen }),
      popOutLayer: () => {
        const { warningsOpen, errorsOpen } = get();
        if (warningsOpen) {
          // TODO pop-out warnings
        } else if (errorsOpen) {
          const folderId = 'SYSTEM_VALIDATION_FOLDER_ID';

          set({ ...whatsOpen });

          //TODO these refs to Decisions.Navigation _disgust me_
          if (getFolderId(usePortalStore.getState())) {
            Decisions.Navigation.openFolderInNewWindow(folderId);
          }
        }
      },
      toggleTools: async () => {
        const toolsOpen = !get().toolsOpen;

        if (toolsOpen) {
          try {
            const toolsActions = await getActionHeadersForAnyEntity({
              actionTypes: [EntityActionType.Silverlight, EntityActionType.WebForm], // what do these mean and why are they hard coded as magic numbers in so many places?
              typeName: 'DecisionsFramework.Design.HelpCenter.HelpCenterTools',
              entityID: 'HELP_CENTER_TOOLS',
            });
            set({ ...whatsOpen, toolsOpen, toolsActions });
          } catch (error) {
            console.error('Error Fetching Tools actions', error);
          }
        } else {
          set({ ...whatsOpen, toolsOpen, toolsActions: [] });
        }
      },
      reRunValidations: () => {
        const { flowSessionId } = get();
        if (flowSessionId) {
          runFlowValidations({ sessionId: flowSessionId });
        }
      },
      ignoreAction: entry => {
        if (entry.ignoreAction) {
          entry.ignoreAction(entry, entry.isIgnored ?? false);
        }
      },
      goToAction: entry => {
        if (entry.gotoAction) {
          entry.gotoAction(entry);
          set({ ...whatsOpen, warningsOpen: false, errorsOpen: false });
        }
      },
      toggleIgnore: () => {
        set({
          ...whatsOpen,
          warningsOpen: get().warningsOpen,
          errorsOpen: get().errorsOpen,
          showIgnored: !get().showIgnored,
        });
      },
    },
  }));

  // TODO move event listener to creation/render of PortalHelpCenter
  if (useStore.getState().helpCenterLocation === HelpCenterLocation.Portal) {
    registerForEventAndSubscribe(SystemValidationChangedEvent, id, async () => {
      asyncQueue({
        id: SystemValidationChangedEvent,
        callback: async () => {
          const issues = await getValidationIssues();
          const entries = issues.map(issue => {
            return new ValidationEntry({
              id: issue.Id,
              title: issue.Name,
              description: issue.Details,
              validationItemType: issue.ValidationItemType,
              entityTypeName: issue.EntityTypeName,
              imageUrl: issue.ImageUrl,
              level: BreakLevel.Fatal,
            });
          });
          useStore.setState({ entries });
        },
        useLastRequestedPromise: true,
      });
    });

    // TODO not sure if FolderChangedMessage is necessary
    registerForEventAndSubscribe(ClientMessage.FolderChanged, id, async e => {
      const details = (e as any).detail;
      if (details.FolderId === 'SYSTEM_VALIDATION_FOLDER_ID') {
        const issues = await getValidationIssues();
        const entries = issues.map(issue => {
          return new ValidationEntry({
            id: issue.Id,
            title: issue.Name,
            description: issue.Details,
            validationItemType: issue.ValidationItemType,
            entityTypeName: issue.EntityTypeName,
            imageUrl: issue.ImageUrl,
            level: BreakLevel.Fatal,
          });
        });
        useStore.setState({ entries });
      }
    });
  }

  return useStore;
}

//#endregion

//#region selector functions

// selector functions could just as easily be inline, but having them pre-defined can DRY up
// reuse, and make them easier to test if they had a complex calculation.
// They also shield components from the effects of store-object refactors.

export function getActions(state: IHelpCenterStore) {
  return state.actions;
}

export function getIssues(state: IHelpCenterStore) {
  return state.entries;
}

export function getWarningsOpen(state: IHelpCenterStore) {
  return state.warningsOpen;
}

export function getErrorsOpen(state: IHelpCenterStore) {
  return state.errorsOpen;
}

export function getChatActive(state: PortalStore) {
  return state.helpChat !== HelpChatState.CLOSED;
}

export function getToolsOpen(state: IHelpCenterStore) {
  return state.toolsOpen;
}

export function getToolsActions(state: IHelpCenterStore) {
  return state.toolsActions;
}

export function getHasIgnoreAction(state: IHelpCenterStore) {
  return state.entries?.some(e => !!e.ignoreAction);
}

export function getShowIgnored(state: IHelpCenterStore) {
  return state.showIgnored;
}

export function getCanPopOut(state: IHelpCenterStore) {
  return state.helpCenterLocation === HelpCenterLocation.Portal;
}

export function getLocation(state: IHelpCenterStore) {
  return state.helpCenterLocation;
}

export function getCanReRunValidations(state: IHelpCenterStore) {
  return state.helpCenterLocation === HelpCenterLocation.Flow;
}

export function getChatCount(state: PortalStore) {
  return state.helpChatCount;
}

export function hasHelpCenterTools(state: PortalStore): boolean {
  return state.portalSettings.HasHelpCenterTools;
}

//#endregion

//#region hooks
// hooks help DRY up boilerplate around getting certain references, e.g.

/** context to pass particular store instance to react children */
export const HelpCenterStore = React.createContext<UseBoundStore<StoreApi<IHelpCenterStore>> | undefined>(undefined);

/**
 * Access particular property of particular store via react context.
 * Must have HelpCenterStore.Provider upstream to use this.
 *
 * This allows what looks like the idiomatic zustand useStore hook to be used in components, but actually
 * receive a particular store instance from the parent, instead of importing a global singleton as a module.
 */
export function useHelpCenterStore<U>(
  selector: StateSelector<IHelpCenterStore, U>,
  equalityFn?: EqualityChecker<U>
): U {
  const useStore = useContext(HelpCenterStore);
  if (!useStore) {
    throw new Error('Mapping Editor store must be configured!');
  }

  return useStore(selector, equalityFn);
}

/**
 * Example of a selector hook that uses local state and memoizes with useCallback
 * @param path some local state you need to use with shared state to pick / derive a value
 * @returns that unique value
 */
export function useSelectorWithLocalState(path: string) {
  return useHelpCenterStore(useCallback(state => (state.entries as any)[path], [path]));
}

//#endregion hooks

//#region sharing store instances with outside world:

/**
 * This kind of StoreMap construct is only necessary if
 * 1. These need to be unique per multiple components, not singletons, AND
 * 1. You need to share state outside React or
 * 1. between react trees that have no common parent.
 *
 * If they were singletons, can just create and export
 * If local, just create/store with `useState(createHelpCenterStore)`
 */
const HelpCenterStoreMap = new Map<string, UseBoundStore<StoreApi<IHelpCenterStore>>>();

/** getter if we need to access from outside React */
export function getHelpCenterStore(someUniqueId: string) {
  return HelpCenterStoreMap.get(someUniqueId);
}

function unCacheStore(someUniqueId: string) {
  HelpCenterStoreMap.delete(someUniqueId);
}

/**
 * A hook you could use to create a store in a parent component for children to consume.
 *
 * Would need to pass the result to children
 *
 * @param helpCenterId id of current flow edit session
 * @param entries validation en
 * @param helpCenterLocation
 * @param flowSessionId
 * @returns existing useStore hook or a new one.
 */
export function useCreateHelpCenterStore(
  helpCenterId: string,
  helpCenterLocation: HelpCenterLocation,
  flowSessionId?: string
) {
  // should create a new store if one of these changes, otherwise return original:
  const useStore = useMemo(() => {
    const store = createHelpCenterStore(helpCenterId);

    // initialize things we know we'll need to initialize
    if (helpCenterLocation === HelpCenterLocation.Portal) {
      store.getState().actions.getValidations();
    }
    store.setState({ helpCenterLocation, flowSessionId });
    return store;
  }, [helpCenterId, helpCenterLocation, flowSessionId]);

  // unmount with designer, for example. Could make a away to preserve these for portal-wide versions.
  useEffect(() => {
    return () => {
      unCacheStore(helpCenterId);
      // remove event listener
      unRegisterForEventAndUnsubscribe(SystemValidationChangedEvent, helpCenterId);
      unRegisterForEventAndUnsubscribe(ClientMessage.FolderChanged, helpCenterId);
    };
  }, [helpCenterId]);

  HelpCenterStoreMap.set(helpCenterId, useStore);

  return useStore;
}

//#endregion sharing store

//#region global state
// this is state that's very specific to help center, but because it's global (not specific to particular help center instances)
// it's really part of PortalStore

export function getHelpCenterState() {
  return {
    helpChat: HelpChatState.CLOSED,
    helpChatInstance: null as any,
    helpChatCount: 0,
  };
}

export function createHelpCenterGlobalActions(get: GetState<PortalStore>, set: SetState<PortalStore>) {
  return {
    /**
     * Toggle it open or closed. Will open if it was closed or minimized.
     */
    toggleChat: () => {
      const state = get();
      let helpChat = state.helpChat;
      let helpChatInstance = state.helpChatInstance;

      if (!DEV_SERVER && !IS_TEST) {
        helpChatInstance = helpChatInstance ?? $DP.Portal.SupportChat.initialize();

        if (helpChat === HelpChatState.OPEN) {
          helpChatInstance.hideChatWindow();
        } else {
          // show whether it's closed or minimized
          helpChatInstance.showChatRoom();
        }
      }
      helpChat =
        helpChat === HelpChatState.MINIMIZED
          ? HelpChatState.OPEN
          : helpChat === HelpChatState.OPEN
          ? HelpChatState.CLOSED
          : HelpChatState.OPEN;

      set({ helpChatInstance, helpChat });
    },

    /**
     * Ending chat will release the instance as well, so if it re-initializes in a different stacking context it'll be on top again.
     */
    endChat: () => set({ helpChatInstance: null, helpChat: HelpChatState.CLOSED }),

    /**
     * Tell the store that it has minimized itself.
     */
    markChatMinimized: () => {
      set({ helpChat: HelpChatState.MINIMIZED });
    },

    setHelpChatCount: (helpChatCount: number) => {
      set({ helpChatCount });
    },
  };
}

//#endregion global state
